// Manifest completo de artículos del Manual de Usuario.
// Toda la búsqueda y el filtrado opera sobre este archivo —
// los Markdown se cargan sólo al abrir un artículo.
//
// Esta es la ÚNICA fuente de contenido del manual: el manual interactivo
// (ManualUsuario.js) y el generador de PDF por rol (pdf/manual/) leen los
// mismos artículos y aplican exactamente la misma regla de visibilidad
// (rolVisibleParaUsuario) — nunca hay dos listas que puedan desincronizarse.

export const ROLES_MANUAL = [
  { id: 'primeros_pasos',      label: 'Primeros pasos',              icono: '🚀', descripcion: 'Orientación inicial para nuevos usuarios' },
  { id: 'general',             label: 'General',                     icono: '🔑', descripcion: 'Acceso, contraseñas y manual' },
  { id: 'recepcionista',       label: 'Recepcionista',                icono: '🏥', descripcion: 'Recepción, turnos, coseguros y gestión de médicos' },
  { id: 'medico',              label: 'Médico',                       icono: '🩺', descripcion: 'Consultas e historia clínica' },
  { id: 'administrador',       label: 'Administrador',                icono: '⚙️', descripcion: 'Configuración y gestión del sistema' },
  { id: 'paciente',            label: 'Paciente',                     icono: '👤', descripcion: 'Turnos y datos personales' },
];

export function rolVisibleParaUsuario(rolManual, rolUsuario) {
  if (['primeros_pasos', 'general'].includes(rolManual)) return true;
  if (rolUsuario === 'admin') return true;
  if (rolUsuario === 'recepcionista') return rolManual === 'recepcionista';
  if (rolUsuario === 'medico') return rolManual === 'medico';
  if (rolUsuario === 'paciente') return rolManual === 'paciente';
  return false;
}

// Categorías (a.rol) que le corresponden a cada rol del sistema, en el orden
// en que deben aparecer en el manual/PDF. Usado por el generador de PDF para
// armar el manual completo de un rol sin repetir la lógica de visibilidad.
export function categoriasDelRol(rolUsuario) {
  return ROLES_MANUAL.filter(r => rolVisibleParaUsuario(r.id, rolUsuario)).map(r => r.id);
}

export const ARTICULOS = [

  // ── PRIMEROS PASOS (todos los roles) ──────────────────────────────────────

  {
    id: 'bienvenido-gestar',
    titulo: 'Bienvenido a Gestar',
    rol: 'primeros_pasos',
    roles: ['admin', 'recepcionista', 'medico', 'paciente'],
    keywords: ['bienvenida', 'introducción', 'qué es gestar', 'presentación', 'inicio'],
    resumen: 'Una presentación breve del sistema: qué es, para qué sirve y cómo está organizado.',
    relacionados: ['como-funciona', 'que-hace-cada-rol'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/primeros-pasos/bienvenido-gestar.md',
  },
  {
    id: 'como-funciona',
    titulo: 'Cómo funciona el sistema',
    rol: 'primeros_pasos',
    roles: ['admin', 'recepcionista', 'medico', 'paciente'],
    keywords: ['funcionamiento', 'flujo general', 'cómo usar', 'lógica del sistema', 'overview'],
    resumen: 'La lógica general del sistema: turnos, coseguros, historia clínica y turnero.',
    relacionados: ['bienvenido-gestar', 'que-hace-cada-rol'],
    tiempoLectura: 3,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/primeros-pasos/como-funciona.md',
  },
  {
    id: 'que-hace-cada-rol',
    titulo: 'Qué hace cada rol en el sistema',
    rol: 'primeros_pasos',
    roles: ['admin', 'recepcionista', 'medico', 'paciente'],
    keywords: ['roles', 'recepcionista', 'médico', 'administrador', 'paciente', 'permisos', 'accesos'],
    resumen: 'Qué puede hacer cada tipo de usuario y cuáles son sus responsabilidades.',
    relacionados: ['bienvenido-gestar', 'como-funciona'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/primeros-pasos/que-hace-cada-rol.md',
  },

  // ── GENERAL (todos los roles) ──────────────────────────────────────────────

  {
    id: 'iniciar-sesion',
    titulo: 'Cómo iniciar sesión',
    rol: 'general',
    roles: ['admin', 'recepcionista', 'medico', 'paciente'],
    keywords: ['iniciar sesión', 'login', 'entrar', 'usuario', 'contraseña', 'acceso'],
    resumen: 'Ingresar al sistema con usuario y contraseña desde cualquier navegador.',
    relacionados: ['cerrar-sesion', 'cambiar-contrasena', 'recuperar-contrasena'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/general/iniciar-sesion.md',
  },
  {
    id: 'cerrar-sesion',
    titulo: 'Cómo cerrar sesión',
    rol: 'general',
    roles: ['admin', 'recepcionista', 'medico', 'paciente'],
    keywords: ['cerrar sesión', 'logout', 'salir', 'desconectar', 'finalizar sesión'],
    resumen: 'Salir del sistema de forma segura para proteger la información del consultorio.',
    relacionados: ['iniciar-sesion'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/general/cerrar-sesion.md',
  },
  {
    id: 'cambiar-contrasena',
    titulo: 'Cómo cambiar mi contraseña',
    rol: 'general',
    roles: ['admin', 'recepcionista', 'medico', 'paciente'],
    keywords: ['cambiar contraseña', 'nueva contraseña', 'modificar contraseña', 'actualizar clave'],
    resumen: 'Cambiar la contraseña de acceso desde la configuración de la cuenta.',
    relacionados: ['recuperar-contrasena', 'iniciar-sesion'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/general/cambiar-contrasena.md',
  },
  {
    id: 'recuperar-contrasena',
    titulo: 'Qué hacer si olvidé mi contraseña',
    rol: 'general',
    roles: ['admin', 'recepcionista', 'medico', 'paciente'],
    keywords: ['contraseña', 'olvidé', 'recuperar', 'nueva contraseña', 'no puedo entrar', 'resetear'],
    resumen: 'Recuperar el acceso al sistema cuando no se recuerda la contraseña.',
    relacionados: ['iniciar-sesion', 'cambiar-contrasena'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/general/recuperar-contrasena.md',
  },
  {
    id: 'usar-manual',
    titulo: 'Cómo usar el Manual de Usuario',
    rol: 'general',
    roles: ['admin', 'recepcionista', 'medico', 'paciente'],
    keywords: ['manual', 'ayuda', 'buscar artículos', 'cómo usar el manual', 'documentación'],
    resumen: 'Cómo navegar el manual, buscar artículos y usar los botones de ayuda contextuales.',
    relacionados: ['bienvenido-gestar'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/general/usar-manual.md',
  },

  // ── RECEPCIONISTA ──────────────────────────────────────────────────────────

  {
    id: 'crear-turno',
    titulo: 'Cómo crear un turno',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['nuevo turno', 'crear turno', 'agendar', 'reservar', 'turno paciente existente'],
    resumen: 'Crear un turno para un paciente que ya está registrado en el sistema.',
    relacionados: ['crear-turno-paciente-nuevo', 'estados-del-turno', 'paciente-sin-dni'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/crear-turno.md',
  },
  {
    id: 'crear-turno-paciente-nuevo',
    titulo: 'Cómo crear un turno para un paciente nuevo',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['paciente nuevo', 'nuevo paciente', 'registrar paciente', 'primer turno'],
    resumen: 'Registrar un paciente por primera vez y crear su turno en el mismo proceso.',
    relacionados: ['crear-turno', 'paciente-sin-dni'],
    tiempoLectura: 3,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/crear-turno-paciente-nuevo.md',
  },
  {
    id: 'recibir-paciente',
    titulo: 'Cómo recibir a un paciente',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['recepción', 'en recepción', 'paciente llega', 'recibir', 'llegada'],
    resumen: 'Registrar la llegada de un paciente y poner el turno en estado "En recepción".',
    relacionados: ['registrar-coseguro', 'estados-del-turno', 'paciente-sin-dni'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/recibir-paciente.md',
  },
  {
    id: 'registrar-coseguro',
    titulo: 'Cómo registrar un coseguro',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['coseguro', 'cobrar', 'monto', 'efectivo', 'pago', 'exento', 'pendiente'],
    resumen: 'Registrar el cobro, la exención o el estado pendiente del coseguro de un turno.',
    relacionados: ['recibir-paciente', 'caja-del-dia'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/registrar-coseguro.md',
  },
  {
    id: 'cancelar-turno',
    titulo: 'Cómo cancelar un turno',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['cancelar', 'anular turno', 'turno cancelado', 'no se presenta'],
    resumen: 'Cancelar un turno y registrar el motivo.',
    relacionados: ['estados-del-turno', 'reprogramar-turno'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/cancelar-turno.md',
  },
  {
    id: 'reprogramar-turno',
    titulo: 'Cómo reprogramar un turno',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['reprogramar', 'cambiar fecha', 'cambiar hora', 'nuevo horario', 'mover turno'],
    resumen: 'Cambiar la fecha u hora de un turno ya agendado.',
    relacionados: ['cancelar-turno', 'crear-turno', 'estados-del-turno'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/reprogramar-turno.md',
  },
  {
    id: 'caja-del-dia',
    titulo: 'Cómo leer la Caja del día',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista', 'medico'],
    keywords: ['caja', 'resumen', 'cobrado', 'total', 'coseguros del día'],
    resumen: 'Interpretar el resumen de coseguros registrados durante el día.',
    relacionados: ['registrar-coseguro'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/caja-del-dia.md',
  },
  {
    id: 'confirmaciones',
    titulo: 'Cómo usar la pantalla Confirmaciones',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['confirmación de asistencia', 'confirmaciones', 'ausencias', 'recordatorio', 'no asistirá'],
    resumen: 'Ver quién confirmó su turno, quién no respondió y quién avisó que no puede venir.',
    relacionados: ['politicas-operativas', 'crear-turno'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-28',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/confirmaciones.md',
  },
  {
    id: 'estados-del-turno',
    titulo: 'Qué significa cada estado del turno',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista', 'medico'],
    keywords: ['estados', 'pendiente', 'confirmado', 'en recepción', 'demorado', 'atendido', 'cancelado'],
    resumen: 'Explicación de cada estado del turno y cuándo se aplica.',
    relacionados: ['recibir-paciente', 'cancelar-turno', 'atender-turno'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/estados-del-turno.md',
  },
  {
    id: 'paciente-sin-dni',
    titulo: 'Qué hacer cuando el sistema no encuentra el DNI',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['DNI', 'no encontrado', 'paciente nuevo', 'sin dni', 'crear paciente', 'no existe'],
    resumen: 'Pasos cuando el sistema no encuentra al paciente al ingresar el DNI.',
    relacionados: ['crear-turno', 'crear-turno-paciente-nuevo'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/paciente-sin-dni.md',
  },
  {
    id: 'usar-calendario',
    titulo: 'Cómo usar el Calendario',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['calendario', 'vista semanal', 'vista día', 'agenda visual', 'arrastrar turno'],
    resumen: 'Ver y gestionar los turnos del día o de la semana desde la vista de Calendario.',
    relacionados: ['crear-turno', 'reprogramar-turno', 'estados-del-turno'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-24',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/usar-calendario.md',
  },

  // ── RECEPCIONISTA: capacidades ampliadas ──────────────────────────────────
  // Desde el 2026-08-01 el rol Recepcionista Administrador desapareció: todo su
  // alcance (gestión de médicos, agenda, corrección de DNI) pasó a Recepcionista.
  // Auditoría queda exclusiva de Administrador — Recepcionista ya no la ve.

  {
    id: 'que-puede-hacer-recepcionista',
    titulo: 'Qué puede hacer el Recepcionista',
    rol: 'recepcionista',
    roles: ['admin', 'recepcionista'],
    keywords: ['recepcionista', 'permisos', 'alcance del rol', 'qué puedo hacer', 'usuarios', 'no puede'],
    resumen: 'El alcance completo del rol: toda la operación diaria más la gestión de médicos y agenda, sin acceso a usuarios, configuración ni auditoría.',
    relacionados: ['crear-medico-recepcionista', 'corregir-dni-paciente-recepcionista'],
    tiempoLectura: 2,
    ultimaRevision: '2026-08-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/recepcionista/que-puede-hacer.md',
  },
  {
    id: 'crear-medico-recepcionista',
    titulo: 'Cómo crear un médico y configurar su disponibilidad',
    rol: 'recepcionista',
    roles: ['recepcionista'],
    keywords: ['médico', 'nuevo médico', 'disponibilidad', 'horarios', 'agenda médico'],
    resumen: 'Registrar un médico con sus datos, especialidad y bloques de atención.',
    relacionados: ['que-puede-hacer-recepcionista', 'ausencia-medico-recepcionista'],
    tiempoLectura: 3,
    ultimaRevision: '2026-08-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/crear-medico.md',
  },
  {
    id: 'ausencia-medico-recepcionista',
    titulo: 'Cómo registrar una ausencia de médico',
    rol: 'recepcionista',
    roles: ['recepcionista'],
    keywords: ['ausencia', 'médico ausente', 'vacaciones', 'cancelar turnos', 'período sin atención'],
    resumen: 'Registrar días de ausencia de un médico y gestionar los turnos afectados.',
    relacionados: ['crear-medico-recepcionista', 'disponibilidad-extraordinaria-recepcionista', 'cancelar-turno'],
    tiempoLectura: 2,
    ultimaRevision: '2026-08-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/ausencia-medico.md',
  },
  {
    id: 'disponibilidad-extraordinaria-recepcionista',
    titulo: 'Cómo registrar disponibilidad extraordinaria',
    rol: 'recepcionista',
    roles: ['recepcionista'],
    keywords: ['disponibilidad extraordinaria', 'horario distinto', 'cubre turno', 'atiende extra', 'excepción de agenda'],
    resumen: 'Registrar que un médico atiende un día puntual con un horario distinto al habitual.',
    relacionados: ['ausencia-medico-recepcionista', 'crear-medico-recepcionista'],
    tiempoLectura: 2,
    ultimaRevision: '2026-08-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/disponibilidad-extraordinaria.md',
  },
  {
    id: 'corregir-dni-paciente-recepcionista',
    titulo: 'Cómo corregir el DNI de un paciente',
    rol: 'recepcionista',
    roles: ['recepcionista'],
    keywords: ['DNI', 'corregir DNI', 'editar DNI', 'paciente', 'identificación', 'dato mal cargado'],
    resumen: 'Cambiar el DNI de un paciente que quedó mal cargado, con motivo obligatorio y auditoría.',
    relacionados: ['que-puede-hacer-recepcionista'],
    tiempoLectura: 2,
    ultimaRevision: '2026-08-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/corregir-dni-paciente.md',
  },

  // ── MÉDICO ─────────────────────────────────────────────────────────────────

  {
    id: 'atender-turno',
    titulo: 'Cómo atender un turno',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['atender', 'consulta', 'abrir turno', 'iniciar consulta'],
    resumen: 'Abrir la pantalla de consulta y comenzar la atención de un paciente.',
    relacionados: ['cargar-evolucion', 'marcar-atendido', 'atender-deshabilitado'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/atender-turno.md',
  },
  {
    id: 'cargar-evolucion',
    titulo: 'Cómo cargar una evolución clínica',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['evolución', 'nota médica', 'diagnóstico', 'medicamentos', 'estudios'],
    resumen: 'Registrar la evolución del paciente durante o después de la consulta.',
    relacionados: ['guardar-evolucion-borrador', 'marcar-atendido', 'ver-historia-clinica', 'evolucion-desde-hc'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/cargar-evolucion.md',
  },
  {
    id: 'guardar-evolucion-borrador',
    titulo: 'Cómo guardar una evolución sin finalizar la consulta',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['guardar borrador', 'evolución parcial', 'guardar y seguir', 'sin cerrar', 'continuar después'],
    resumen: 'Guardar el avance de la evolución para retomarlo sin marcar el turno como atendido.',
    relacionados: ['cargar-evolucion', 'marcar-atendido'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/guardar-evolucion-borrador.md',
  },
  {
    id: 'marcar-atendido',
    titulo: 'Cómo marcar un turno como atendido',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['atendido', 'cerrar turno', 'finalizar consulta', 'guardar y atendido'],
    resumen: 'Finalizar la consulta y cambiar el estado del turno a "Atendido".',
    relacionados: ['cargar-evolucion', 'guardar-evolucion-borrador'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/marcar-atendido.md',
  },
  {
    id: 'ver-historia-clinica',
    titulo: 'Cómo ver la historia clínica de un paciente',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['historia clínica', 'HC', 'antecedentes', 'evoluciones previas', 'historial'],
    resumen: 'Acceder al historial médico completo de un paciente.',
    relacionados: ['cargar-evolucion', 'evolucion-desde-hc', 'sin-acceso-hc', 'anular-evolucion'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/ver-historia-clinica.md',
  },
  {
    id: 'evolucion-desde-hc',
    titulo: 'Cómo cargar una evolución desde la Historia Clínica',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['nueva evolución', 'fuera de turno', 'turno compatible', 'evolución sin turno', 'historia clínica'],
    resumen: 'Registrar una evolución sin pasar por Atender turno, con verificación de turno compatible.',
    relacionados: ['cargar-evolucion', 'ver-historia-clinica', 'anular-evolucion'],
    tiempoLectura: 2,
    ultimaRevision: '2026-08-14',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/evolucion-desde-hc.md',
  },
  {
    id: 'llamar-turnero',
    titulo: 'Cómo llamar a un paciente al Turnero',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['turnero', 'llamar', 'pantalla sala de espera', 'consultorio', 'llamado'],
    resumen: 'Enviar el nombre del paciente a la pantalla de sala de espera.',
    relacionados: ['atender-turno', 'estados-del-turno'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/llamar-turnero.md',
  },
  {
    id: 'atender-deshabilitado',
    titulo: 'Por qué el botón "Atender" no está disponible',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['atender grisado', 'botón deshabilitado', 'no puedo atender', 'atender bloqueado'],
    resumen: 'Causas por las que el botón Atender aparece inactivo y cómo resolverlas.',
    relacionados: ['estados-del-turno', 'atender-turno'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/atender-deshabilitado.md',
  },
  {
    id: 'sin-acceso-hc',
    titulo: 'Por qué no puedo ver la historia clínica de un paciente',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['sin acceso HC', 'historia clínica bloqueada', 'no puedo ver HC', 'acceso denegado'],
    resumen: 'Razones por las que aparece el mensaje "Sin acceso" al intentar abrir una HC.',
    relacionados: ['ver-historia-clinica', 'atender-turno'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/sin-acceso-hc.md',
  },
  {
    id: 'anular-evolucion',
    titulo: 'Cómo anular una evolución',
    rol: 'medico',
    roles: ['admin', 'medico'],
    keywords: ['anular evolución', 'error en evolución', 'corregir nota', 'eliminar evolución'],
    resumen: 'Invalidar una evolución cargada con error y registrar el motivo.',
    relacionados: ['cargar-evolucion', 'ver-historia-clinica'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/medico/anular-evolucion.md',
  },

  // ── ADMINISTRADOR ──────────────────────────────────────────────────────────

  {
    id: 'crear-medico',
    titulo: 'Cómo crear un médico y configurar su disponibilidad',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['médico', 'nuevo médico', 'disponibilidad', 'horarios', 'agenda médico'],
    resumen: 'Registrar un médico con sus datos, especialidad y bloques de atención.',
    relacionados: ['vincular-usuario-medico', 'ausencia-medico'],
    tiempoLectura: 3,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/crear-medico.md',
  },
  {
    id: 'administrar-usuarios',
    titulo: 'Cómo administrar usuarios y permisos',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['usuario', 'staff', 'nuevo usuario', 'crear cuenta', 'permisos', 'acceso', 'roles', 'cambiar contraseña usuario', 'bloquear usuario'],
    resumen: 'Crear, modificar y gestionar las cuentas de acceso de todo el personal.',
    relacionados: ['vincular-usuario-medico'],
    tiempoLectura: 3,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/administrar-usuarios.md',
  },
  {
    id: 'vincular-usuario-medico',
    titulo: 'Cómo vincular un usuario con un médico',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['vincular médico', 'medico_id', 'usuario médico', 'asociar cuenta'],
    resumen: 'Conectar una cuenta de acceso con la ficha del médico correspondiente.',
    relacionados: ['administrar-usuarios', 'crear-medico'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/vincular-usuario-medico.md',
  },
  {
    id: 'ausencia-medico',
    titulo: 'Cómo registrar una ausencia de médico',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['ausencia', 'médico ausente', 'vacaciones', 'cancelar turnos', 'período sin atención'],
    resumen: 'Registrar días de ausencia de un médico y gestionar los turnos afectados.',
    relacionados: ['crear-medico', 'disponibilidad-extraordinaria', 'cancelar-turno'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-28',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/ausencia-medico.md',
  },
  {
    id: 'disponibilidad-extraordinaria',
    titulo: 'Cómo registrar disponibilidad extraordinaria',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['disponibilidad extraordinaria', 'horario distinto', 'cubre turno', 'atiende extra', 'excepción de agenda'],
    resumen: 'Registrar que un médico atiende un día puntual con un horario distinto al habitual.',
    relacionados: ['ausencia-medico', 'crear-medico'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-28',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/disponibilidad-extraordinaria.md',
  },
  {
    id: 'pin-turnero',
    titulo: 'Cómo gestionar el PIN del Turnero',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['PIN', 'turnero', 'pantalla TV', 'sala de espera', 'regenerar PIN'],
    resumen: 'Ver, comunicar y regenerar el PIN de acceso a la pantalla del Turnero.',
    relacionados: ['llamar-turnero'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/pin-turnero.md',
  },
  {
    id: 'anular-coseguro',
    titulo: 'Cómo anular o corregir un coseguro',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['anular coseguro', 'coseguro anulado', 'deshacer cobro', 'reversar', 'corregir coseguro', 'coseguro día anterior'],
    resumen: 'Anular un coseguro registrado, incluyendo correcciones de días anteriores.',
    relacionados: ['registrar-coseguro'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/anular-coseguro.md',
  },
  {
    id: 'auditoria-sistema',
    titulo: 'Cómo consultar la auditoría del sistema',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['auditoría', 'historial de cambios', 'log', 'quién cambió', 'trazabilidad', 'registro de acciones'],
    resumen: 'Consultar el registro de cambios de estado, acciones del personal y eventos del sistema.',
    relacionados: ['estados-del-turno', 'administrar-usuarios'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/auditoria-sistema.md',
  },
  {
    id: 'auditoria-medicos',
    titulo: 'Cómo consultar la Auditoría de Médicos',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['auditoría', 'auditoría de médicos', 'historial de cambios', 'quién cambió', 'especialidades', 'disponibilidad'],
    resumen: 'Consultar qué cambios se hicieron sobre médicos, especialidades, disponibilidad, ausencias y pacientes.',
    relacionados: ['auditoria-sistema', 'crear-medico', 'corregir-dni-paciente'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-24',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/auditoria-medicos.md',
  },
  {
    id: 'corregir-dni-paciente',
    titulo: 'Cómo corregir el DNI de un paciente',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['DNI', 'corregir DNI', 'editar DNI', 'paciente', 'identificación', 'dato mal cargado'],
    resumen: 'Cambiar el DNI de un paciente que quedó mal cargado, con motivo obligatorio y auditoría.',
    relacionados: ['auditoria-medicos', 'administrar-usuarios'],
    tiempoLectura: 2,
    ultimaRevision: '2026-08-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/corregir-dni-paciente.md',
  },
  {
    id: 'politicas-operativas',
    titulo: 'Cómo configurar las Políticas Operativas',
    rol: 'administrador',
    roles: ['admin'],
    keywords: ['políticas operativas', 'evolución obligatoria', 'mínimo de caracteres', 'borrador de evolución', 'configuración'],
    resumen: 'Configurar reglas propias de la organización: evolución obligatoria y borradores de evolución.',
    relacionados: ['guardar-evolucion-borrador', 'cargar-evolucion'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-24',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/administrador/politicas-operativas.md',
  },

  // ── PACIENTE ───────────────────────────────────────────────────────────────

  {
    id: 'ver-mis-turnos',
    titulo: 'Cómo ver mis turnos',
    rol: 'paciente',
    roles: ['paciente'],
    keywords: ['mis turnos', 'ver turnos', 'lista de turnos', 'panel paciente'],
    resumen: 'Acceder al panel personal y consultar el historial de turnos.',
    relacionados: ['cancelar-mi-turno', 'reprogramar-mi-turno'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/paciente/ver-mis-turnos.md',
  },
  {
    id: 'cancelar-mi-turno',
    titulo: 'Cómo cancelar un turno',
    rol: 'paciente',
    roles: ['paciente'],
    keywords: ['cancelar turno', 'anular mi turno', 'no puedo ir'],
    resumen: 'Cancelar un turno pendiente desde el portal del paciente.',
    relacionados: ['ver-mis-turnos', 'reprogramar-mi-turno'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/paciente/cancelar-mi-turno.md',
  },
  {
    id: 'reprogramar-mi-turno',
    titulo: 'Cómo reprogramar un turno',
    rol: 'paciente',
    roles: ['paciente'],
    keywords: ['reprogramar', 'cambiar fecha', 'cambiar hora', 'nuevo horario'],
    resumen: 'Cambiar la fecha y hora de un turno existente desde el portal.',
    relacionados: ['ver-mis-turnos', 'cancelar-mi-turno'],
    tiempoLectura: 1,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/paciente/reprogramar-mi-turno.md',
  },
  {
    id: 'activar-cuenta',
    titulo: 'Cómo activar mi cuenta en Gestar',
    rol: 'paciente',
    roles: ['paciente'],
    keywords: ['activar cuenta', 'primera vez', 'crear contraseña', 'registrarme', 'acceso paciente'],
    resumen: 'Crear acceso al portal para pacientes que ya fueron atendidos en el consultorio.',
    relacionados: ['ver-mis-turnos', 'recuperar-contrasena'],
    tiempoLectura: 2,
    ultimaRevision: '2026-07-01',
    versionSistema: '1.0',
    archivo: '/api/ayuda-content/paciente/activar-cuenta.md',
  },
];

export function buscarArticulos(query, rolUsuario) {
  const q = query.toLowerCase().trim();
  const base = ARTICULOS.filter(a => a.roles.includes(rolUsuario) || rolUsuario === 'admin');
  if (!q) return base;
  return base.filter(a =>
    a.titulo.toLowerCase().includes(q) ||
    a.resumen.toLowerCase().includes(q) ||
    a.keywords.some(k => k.toLowerCase().includes(q))
  );
}

export function articulosPorRol(rol) {
  if (rol === 'admin') return ARTICULOS;
  return ARTICULOS.filter(a => a.roles.includes(rol));
}

export function articuloPorId(id) {
  return ARTICULOS.find(a => a.id === id) || null;
}
